import { ArrowRight, ArrowLeft, Shield, Search, Target, Wifi, Network, Users, Eye, Zap, Lock, Terminal, Activity, CheckCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';

const OffensiveSecurity = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);

  const offensiveServices = [
    {
      icon: <Network className="w-8 h-8" />,
      title: "Network Penetration Testing",
      subtitle: "Realistic assessment of network vulnerabilities and attack paths",
      description: "Comprehensive network security testing that identifies vulnerabilities across your entire infrastructure, from perimeter defenses to internal network segments.",
      gradient: "linear-gradient(135deg, #f87171 0%, #fbbf24 100%)",
      borderColor: "rgba(248, 113, 113, 0.3)",
      iconColor: "#f87171",
      capabilities: [
        {
          title: "External Network Penetration Testing",
          description: "Internet-facing asset assessment and attack path discovery"
        },
        {
          title: "Internal Network Penetration Testing",
          description: "Privilege escalation and lateral movement simulation"
        },
        {
          title: "Network Segmentation & Lateral Movement Testing",
          description: "Validation of network isolation and containment controls"
        },
        {
          title: "Wireless Penetration Testing",
          description: "Wi-Fi security assessment and rogue access point detection"
        }
      ]
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Red Team Assessment",
      subtitle: "Full-scope adversary emulation that exposes gaps in detection and response",
      description: "Advanced adversary simulation that tests your entire security program through realistic, multi-stage attack campaigns designed to evade detection.",
      gradient: "linear-gradient(135deg, #dc2626 0%, #ea580c 100%)",
      borderColor: "rgba(220, 38, 38, 0.3)",
      iconColor: "#dc2626",
      capabilities: [
        {
          title: "Simulated Adversary Campaigns",
          description: "Multi-phase attacks mimicking real-world threat actors"
        },
        {
          title: "Stealth and Evasion Focus",
          description: "Testing detection capabilities with advanced evasion techniques"
        },
        {
          title: "Objective-Based Operations",
          description: "Goal-oriented testing aligned with business-critical assets"
        },
        {
          title: "End-to-End Attack Chain Coverage",
          description: "Complete kill chain execution from initial access to objective completion"
        }
      ]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Purple Team Engagement",
      subtitle: "Collaborative testing that strengthens your security posture",
      description: "Unified offensive and defensive testing approach that simultaneously validates detection capabilities while improving defensive responses through real-time collaboration.",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
      borderColor: "rgba(124, 58, 237, 0.3)",
      iconColor: "#7c3aed",
      capabilities: [
        {
          title: "Collaborative Attack Simulation",
          description: "Joint red and blue team exercises with real-time feedback"
        },
        {
          title: "Detection & Response Enhancement",
          description: "SOC team training through live attack scenarios"
        },
        {
          title: "MITRE ATT&CK Mapping & Threat Alignment",
          description: "Framework-based testing aligned with industry threat models"
        },
        {
          title: "Knowledge Transfer & Skill Building",
          description: "Defensive team capability development through practical training"
        }
      ]
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: "Application Security Testing",
      subtitle: "Comprehensive application penetration testing across all platforms",
      description: "In-depth security assessment of web applications, mobile apps, APIs, and source code to identify vulnerabilities before they can be exploited by attackers.",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      borderColor: "rgba(16, 185, 129, 0.3)",
      iconColor: "#10b981",
      capabilities: [
        {
          title: "Web Application Penetration Testing",
          description: "Manual testing of web applications for business logic flaws and vulnerabilities"
        },
        {
          title: "Mobile Application Security Testing",
          description: "iOS and Android application security assessment and reverse engineering"
        },
        {
          title: "API Security Testing",
          description: "REST, GraphQL, and SOAP API endpoint security validation and testing"
        },
        {
          title: "Source Code Security Review",
          description: "Manual code review and static analysis for security vulnerabilities"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Real-World Attack Simulation",
      description: "Techniques used by actual threat actors, not just vulnerability scanners",
      color: "#f87171"
    },
    {
      icon: <Eye className="w-12 h-12" />,
      title: "Detection Gap Identification",
      description: "Expose blind spots in monitoring and incident response capabilities",
      color: "#fbbf24"
    },
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: "Actionable Remediation",
      description: "Clear guidance with proof-of-concept demonstrations and priority rankings",
      color: "#00a86b"
    }
  ];

  return (
    <PageLayout>
      <SEO 
        title="Penetration Testing & Offensive Security | Red Team & Ethical Hacking"
        description="Elite penetration testing and offensive security services including web application testing, network penetration testing, red team assessments, and ethical hacking by certified security experts. Identify vulnerabilities before attackers do."
        keywords={[
          'penetration testing',
          'offensive security',
          'red team testing',
          'ethical hacking',
          'web application penetration testing',
          'network penetration testing',
          'cloud penetration testing',
          'mobile app penetration testing',
          'API penetration testing',
          'vulnerability assessment',
          'security testing services',
          'penetration testing company',
          'cyber security testing',
          'red team exercises',
          'purple team assessment'
        ]}
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 10% 20%, rgba(248, 113, 113, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 90% 80%, rgba(220, 38, 38, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/services"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(248, 113, 113, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-block mb-4 px-6 py-3 text-sm font-medium border shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                  color: "#ffffff",
                  borderRadius: "999px",
                  borderColor: "rgba(248, 113, 113, 0.3)",
                  borderWidth: 1.5,
                  boxShadow: "0 4px 24px 0 rgba(248, 113, 113, 0.25)",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                <Target className="w-4 h-4 inline mr-2" />
                Offensive Security
              </div>
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#f87171",
                    fontWeight: 700,
                  }}
                >
                  Think Like an Attacker
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  Test Your Defenses with Real-World Tactics
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Elite offensive security testing by senior experts who think like attackers. 
                We use real-world tactics to find vulnerabilities and test your defenses before malicious actors can.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Comprehensive Offensive Security Services
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                From network penetration testing to full adversary simulation
              </p>
            </motion.div>

            {/* Service Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {offensiveServices.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-3 py-4 rounded-lg font-medium transition-all duration-300 text-center ${
                    activeService === index ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: activeService === index ? service.gradient : 'rgba(15, 15, 23, 0.85)',
                    border: `1.5px solid ${activeService === index ? service.borderColor : 'rgba(255, 255, 255, 0.1)'}`,
                    color: "#fff",
                    minHeight: "100px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "16px 12px"
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {service.icon}
                  </div>
                  <span className="text-xs sm:text-sm leading-tight" style={{ wordBreak: "break-word" }}>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Service Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card
                  className="mb-8"
                  style={{
                    background: "rgba(15, 15, 23, 0.85)",
                    border: `2px solid ${offensiveServices[activeService].borderColor}`,
                    borderRadius: 16,
                  }}
                >
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-8">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          background: offensiveServices[activeService].gradient,
                          borderRadius: 16,
                        }}
                      >
                        {offensiveServices[activeService].icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl md:text-3xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          {offensiveServices[activeService].title}
                        </h3>
                        <p
                          className="text-base sm:text-lg"
                          style={{
                            color: offensiveServices[activeService].iconColor,
                            fontWeight: 600,
                            marginBottom: 16,
                          }}
                        >
                          {offensiveServices[activeService].subtitle}
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.7,
                          }}
                        >
                          {offensiveServices[activeService].description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {offensiveServices[activeService].capabilities.map((capability, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-6 rounded-lg"
                          style={{
                            background: "rgba(0, 0, 0, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className="flex items-start">
                            <Target 
                              className="w-5 h-5 mr-3 mt-1 flex-shrink-0" 
                              style={{ color: offensiveServices[activeService].iconColor }} 
                            />
                            <div className="flex-1">
                              <h4
                                className="text-base sm:text-lg"
                                style={{
                                  fontWeight: 600,
                                  color: "#fff",
                                  marginBottom: 8,
                                }}
                              >
                                {capability.title}
                              </h4>
                              <p
                                className="text-xs sm:text-sm"
                                style={{
                                  color: "#9ca3af",
                                  lineHeight: 1.6,
                                }}
                              >
                                {capability.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Benefits Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="flex items-center justify-center mx-auto mb-4" 
                    style={{ 
                      color: benefit.color,
                      width: "48px",
                      height: "48px"
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, color: "#fff", marginBottom: 8, fontSize: 20 }}>
                    {benefit.title}
                  </h4>
                  <p style={{ color: "#9ca3af", fontSize: 15, lineHeight: 1.6 }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 text-center"
              style={{
                background: "rgba(15, 15, 23, 0.85)",
                borderRadius: 16,
                padding: "3rem",
                border: "1px solid rgba(248, 113, 113, 0.2)",
              }}
            >
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                Test Your Security Posture
              </h3>
              <p
                style={{
                  color: "#d1d5db",
                  fontSize: 18,
                  marginBottom: 32,
                  maxWidth: 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Discover vulnerabilities and test your defenses with real-world attack techniques. 
                Schedule your offensive security assessment with our senior experts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contactus"
                  className="inline-flex items-center font-medium transition-all shadow-lg hover:shadow-xl"
                  style={{
                    background: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                    color: "#ffffff",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    boxShadow: "0 4px 24px 0 rgba(248, 113, 113, 0.25)",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #f87171 0%, #dc2626 100%)";
                  }}
                >
                  Schedule Security Assessment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium transition-all"
                  style={{
                    background: "transparent",
                    color: "#f87171",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    border: "1.5px solid #f87171",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "rgba(248, 113, 113, 0.1)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default OffensiveSecurity;